import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, CheckCircleIcon, XCircleIcon, SpeakerWaveIcon, PhotoIcon } from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTheme } from '../contexts/ThemeContext';
import { CreateCardDto } from '../types';
import { CARD_LIMITS, getTextLength, validateCardContent } from '../utils/cardLimits';
import { t } from '../utils/translations';
import { ttsService } from '../utils/textToSpeech';
import toast from 'react-hot-toast';
import ImageLightbox from './ImageLightbox';

interface AddCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (cardData: CreateCardDto) => Promise<void>;
  subscription?: any;
  cardCount: number;
  checkLimit: (type: 'card' | 'deck', current?: number) => boolean;
  onUpgradePrompt: () => void;
}

const AddCardModal: React.FC<AddCardModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  subscription, 
  cardCount, 
  checkLimit, 
  onUpgradePrompt 
}) => {
  const { isDark } = useTheme();
  const [cardForm, setCardForm] = useState<CreateCardDto>({
    front: '',
    back: '',
    difficulty: 1,
    tags: []
  });
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'front' | 'back' | 'details'>('front');

  // Character count tracking
  const [frontCharCount, setFrontCharCount] = useState(0);
  const [backCharCount, setBackCharCount] = useState(0);
  
  // TTS states
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentlyPlayingText, setCurrentlyPlayingText] = useState<string>('');
  
  // Error message tracking to prevent multiple toasts
  const [frontErrorShown, setFrontErrorShown] = useState(false);
  const [backErrorShown, setBackErrorShown] = useState(false);

  // Image upload states
  const [frontImageFile, setFrontImageFile] = useState<File | null>(null);
  const [backImageFile, setBackImageFile] = useState<File | null>(null);
  const [uploadingFrontImage, setUploadingFrontImage] = useState(false);
  const [uploadingBackImage, setUploadingBackImage] = useState(false);

  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCardForm({
        front: '',
        back: '',
        difficulty: 1,
        tags: [],
        frontImage: undefined,
        backImage: undefined
      });
      setActiveTab('front');
      setFrontCharCount(0);
      setBackCharCount(0);
      setFrontErrorShown(false);
      setBackErrorShown(false);
      setFrontImageFile(null);
      setBackImageFile(null);
    }
  }, [isOpen]);

  // Handle front content change with character counting
  const handleFrontChange = (value: string) => {
    setCardForm(prev => ({ ...prev, front: value }));
    const charCount = getTextLength(value);
    setFrontCharCount(charCount);
    
    if (charCount > CARD_LIMITS.MAX_FRONT_LENGTH) {
      // Only show toast if we haven't already shown it
      if (!frontErrorShown) {
        toast.error(`Texto da pergunta excede ${CARD_LIMITS.MAX_FRONT_LENGTH} caracteres (${charCount})`);
        setFrontErrorShown(true);
      }
    } else {
      // Reset error state when back under limit
      setFrontErrorShown(false);
    }
  };

  // Handle back content change with character counting
  const handleBackChange = (value: string) => {
    setCardForm(prev => ({ ...prev, back: value }));
    const charCount = getTextLength(value);
    setBackCharCount(charCount);
    
    if (charCount > CARD_LIMITS.MAX_BACK_LENGTH) {
      // Only show toast if we haven't already shown it
      if (!backErrorShown) {
        toast.error(`Texto da resposta excede ${CARD_LIMITS.MAX_BACK_LENGTH} caracteres (${charCount})`);
        setBackErrorShown(true);
      }
    } else {
      // Reset error state when back under limit
      setBackErrorShown(false);
    }
  };

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline']
    ]
  };

  const quillFormats = [
    'bold', 'italic', 'underline'
  ];

  const handleSave = async () => {
    // Check card limit before saving
    if (subscription && subscription.subscriptionTier === 'free' && !checkLimit('card', cardCount)) {
      onUpgradePrompt();
      return;
    }
    
    // Validate content using shared validation utility
    const validation = validateCardContent(cardForm.front, cardForm.back);
    
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave(cardForm);
      onClose();
    } catch (error) {
      console.error('Failed to save card:', error);
      toast.error('Failed to save card');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setCardForm({
      front: '',
      back: '',
      difficulty: 1,
      tags: [],
      frontImage: undefined,
      backImage: undefined
    });
    setFrontCharCount(0);
    setBackCharCount(0);
    setFrontImageFile(null);
    setBackImageFile(null);
    onClose();
  };

  const handleImageUpload = async (file: File, side: 'front' | 'back') => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato de imagem inválido. Use JPG, PNG, GIF ou WebP.');
      return;
    }

    // Validate file size (2MB limit)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      toast.error('Imagem muito grande. O tamanho máximo é 2MB.');
      return;
    }

    if (side === 'front') {
      setUploadingFrontImage(true);
    } else {
      setUploadingBackImage(true);
    }

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        if (side === 'front') {
          setCardForm(prev => ({ ...prev, frontImage: base64String }));
          setFrontImageFile(file);
          setUploadingFrontImage(false);
        } else {
          setCardForm(prev => ({ ...prev, backImage: base64String }));
          setBackImageFile(file);
          setUploadingBackImage(false);
        }
        toast.success('Imagem carregada com sucesso!');
      };
      reader.onerror = () => {
        toast.error('Erro ao carregar a imagem.');
        if (side === 'front') {
          setUploadingFrontImage(false);
        } else {
          setUploadingBackImage(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Erro ao processar a imagem.');
      if (side === 'front') {
        setUploadingFrontImage(false);
      } else {
        setUploadingBackImage(false);
      }
    }
  };

  const handleRemoveImage = (side: 'front' | 'back') => {
    if (side === 'front') {
      setCardForm(prev => ({ ...prev, frontImage: undefined }));
      setFrontImageFile(null);
      // Reset file input
      const fileInput = document.getElementById('frontImageFile') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } else {
      setCardForm(prev => ({ ...prev, backImage: undefined }));
      setBackImageFile(null);
      // Reset file input
      const fileInput = document.getElementById('backImageFile') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }
  };

  const handlePlayAudio = async (text: string) => {
    if (!ttsService.getIsSupported()) {
      toast.error(t('tts.notSupported'));
      return;
    }

    try {
      // Stop any currently playing audio
      if (isSpeaking) {
        ttsService.stop();
        setIsSpeaking(false);
        setCurrentlyPlayingText('');
        return;
      }

      setIsSpeaking(true);
      setCurrentlyPlayingText(text);

      // Uses ElevenLabs with automatic language detection
      await ttsService.speak(text);

      setIsSpeaking(false);
      setCurrentlyPlayingText('');
    } catch (error) {
      console.error('TTS Error:', error);
      toast.error(t('tts.error'));
      setIsSpeaking(false);
      setCurrentlyPlayingText('');
    }
  };

  // Clean up TTS when component unmounts
  useEffect(() => {
    return () => {
      if (ttsService.isSpeaking()) {
        ttsService.stop();
        setIsSpeaking(false);
        setCurrentlyPlayingText('');
      }
    };
  }, []);

  if (!isOpen) return null;

  const isLimitReached = subscription && subscription.subscriptionTier === 'free' && !checkLimit('card', cardCount);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={handleCancel}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="sm:flex sm:items-start">
              <div className="w-full">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <PlusIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 mr-3" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {t('cards.addNewCard')}
                    </h3>
                  </div>
                  <button
                    onClick={handleCancel}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Warning for card limits */}
          {subscription && subscription.subscriptionTier === 'free' && (
            <>
              {cardCount >= (subscription.limits.maxCardsPerDeck || 100) * 0.8 && cardCount < (subscription.limits.maxCardsPerDeck || 100) && (
                <div className="mx-4 mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-amber-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 15.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      <strong>{t('cards.approachingLimit')}</strong> {t('cards.approachingLimitMessage').replace('{remaining}', String((subscription.limits.maxCardsPerDeck || 100) - cardCount))}
                    </p>
                  </div>
                </div>
              )}
              {cardCount >= (subscription.limits.maxCardsPerDeck || 100) && (
                <div className="mx-4 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      <strong>{t('cards.cardLimitReached')}:</strong> {t('cards.cardLimitReachedMessage').replace('{limit}', String(subscription.limits.maxCardsPerDeck || 100))} 
                      <button 
                        type="button"
                        onClick={onUpgradePrompt}
                        className="ml-1 underline hover:no-underline font-medium"
                      >
                        {t('cards.upgradeToUnlimited')}
                      </button>.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Tab Navigation */}
          <div className="px-4 pt-4 border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('front')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'front'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {t('cards.front')}
              </button>
              <button
                onClick={() => setActiveTab('back')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'back'
                    ? 'border-green-500 text-green-600 dark:text-green-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                {t('cards.back')}
              </button>
              <button
                onClick={() => setActiveTab('details')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Detalhes
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="px-4 pt-5 pb-4 sm:p-6">
            {activeTab === 'front' ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('cards.front')} *
                  </label>
                  <button
                    onClick={() => handlePlayAudio(cardForm.front)}
                    disabled={!cardForm.front.trim()}
                    className={`inline-flex items-center justify-center w-8 h-8 border text-xs font-medium rounded-full transition-all duration-200 shadow-sm ${
                      !cardForm.front.trim() 
                        ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 cursor-not-allowed opacity-50' 
                        : isSpeaking && currentlyPlayingText === cardForm.front
                          ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md'
                    }`}
                    title={isSpeaking ? t('tts.stopAudio') : t('tts.playAudio')}
                  >
                    <SpeakerWaveIcon className={`h-4 w-4 ${isSpeaking && currentlyPlayingText === cardForm.front ? 'animate-pulse' : ''}`} />
                  </button>
                </div>
                <ReactQuill
                  key="front-editor"
                  value={cardForm.front}
                  onChange={handleFrontChange}
                  modules={quillModules}
                  formats={quillFormats}
                  className={`${isDark ? 'dark-quill' : ''}`}
                  theme="snow"
                  placeholder={t('cards.enterQuestion')}
                  style={{
                    backgroundColor: isDark ? '#374151' : '#ffffff',
                    color: isDark ? '#ffffff' : '#000000',
                    minHeight: '200px'
                  }}
                />
                <div className="flex justify-between items-center mt-1">
                  <div className={`text-xs ${
                    frontCharCount > CARD_LIMITS.MAX_FRONT_LENGTH 
                      ? 'text-red-600 dark:text-red-400 font-medium' 
                      : frontCharCount > CARD_LIMITS.MAX_FRONT_LENGTH * 0.8 
                        ? 'text-amber-600 dark:text-amber-400' 
                        : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {frontCharCount} / {CARD_LIMITS.MAX_FRONT_LENGTH} caracteres
                  </div>
                  {frontCharCount > CARD_LIMITS.MAX_FRONT_LENGTH && (
                    <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                      Excesso: {frontCharCount - CARD_LIMITS.MAX_FRONT_LENGTH}
                    </div>
                  )}
                </div>
              </div>
            ) : activeTab === 'back' ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('cards.back')} *
                  </label>
                  <button
                    onClick={() => handlePlayAudio(cardForm.back)}
                    disabled={!cardForm.back.trim()}
                    className={`inline-flex items-center justify-center w-8 h-8 border text-xs font-medium rounded-full transition-all duration-200 shadow-sm ${
                      !cardForm.back.trim() 
                        ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 cursor-not-allowed opacity-50' 
                        : isSpeaking && currentlyPlayingText === cardForm.back
                          ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-md'
                    }`}
                    title={isSpeaking ? t('tts.stopAudio') : t('tts.playAudio')}
                  >
                    <SpeakerWaveIcon className={`h-4 w-4 ${isSpeaking && currentlyPlayingText === cardForm.back ? 'animate-pulse' : ''}`} />
                  </button>
                </div>
                <ReactQuill
                  key="back-editor"
                  value={cardForm.back}
                  onChange={handleBackChange}
                  modules={quillModules}
                  formats={quillFormats}
                  className={`${isDark ? 'dark-quill' : ''}`}
                  theme="snow"
                  placeholder={t('cards.enterAnswer')}
                  style={{
                    backgroundColor: isDark ? '#374151' : '#ffffff',
                    color: isDark ? '#ffffff' : '#000000',
                    minHeight: '200px'
                  }}
                />
                <div className="flex justify-between items-center mt-1">
                  <div className={`text-xs ${
                    backCharCount > CARD_LIMITS.MAX_BACK_LENGTH 
                      ? 'text-red-600 dark:text-red-400 font-medium' 
                      : backCharCount > CARD_LIMITS.MAX_BACK_LENGTH * 0.8 
                        ? 'text-amber-600 dark:text-amber-400' 
                        : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {backCharCount} / {CARD_LIMITS.MAX_BACK_LENGTH} caracteres
                  </div>
                  {backCharCount > CARD_LIMITS.MAX_BACK_LENGTH && (
                    <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                      Excesso: {backCharCount - CARD_LIMITS.MAX_BACK_LENGTH}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Difficulty Selection */}
                <div>
                  <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('cards.difficulty')}
                  </label>
                  <select
                    id="difficulty"
                    value={cardForm.difficulty}
                    onChange={(e) => setCardForm(prev => ({ ...prev, difficulty: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value={1}>{t('cards.difficulties.1')}</option>
                    <option value={2}>{t('cards.difficulties.2')}</option>
                    <option value={3}>{t('cards.difficulties.3')}</option>
                    <option value={4}>{t('cards.difficulties.4')}</option>
                    <option value={5}>{t('cards.difficulties.5')}</option>
                  </select>
                </div>

                {/* Tags Input */}
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('cards.tags')} ({t('cards.optional')})
                  </label>
                  <input
                    type="text"
                    id="tags"
                    value={cardForm.tags?.join(', ') || ''}
                    onChange={(e) => {
                      const tags = e.target.value
                        .split(',')
                        .map(tag => tag.trim())
                        .filter(tag => tag.length > 0);
                      setCardForm(prev => ({ ...prev, tags }));
                    }}
                    placeholder={t('cards.tagPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Separe as tags com vírgulas (ex: vocabulário, básico, verbo)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse bg-gray-50 dark:bg-gray-700">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || isLimitReached}
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                isLimitReached 
                  ? 'bg-gray-400 dark:bg-gray-600' 
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-600'
              }`}
            >
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              {isSaving ? 'Salvando...' : isLimitReached ? t('cards.cardLimitReached') : t('cards.addCard')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSaving}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircleIcon className="h-4 w-4 mr-2" />
              Cancelar
            </button>
          </div>
        </div>
      </div>
      
      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        imageUrl={lightboxImage}
        onClose={() => setLightboxOpen(false)}
        alt="Card Image"
      />
    </div>
  );
};

export default AddCardModal;